import { Genre } from '../types';

export interface GenrePresetConfig {
  name: string;
  description: string;
  icon: string;
  // Matchering algorithm parameters
  threshold?: number;        // Limiter threshold (0-1)
  lowessFrac?: number;       // EQ smoothing (0.01-0.15)
  lowessIt?: number;         // LOWESS iterations
  fftSize?: number;          // FFT size for frequency analysis
  useLimiter?: boolean;      // Enable/disable limiter
  normalize?: boolean;       // Enable/disable normalization
}

export const GENRE_PRESETS: Record<Genre, GenrePresetConfig> = {
  auto: {
    name: 'Auto',
    description: 'Automatically detect best settings',
    icon: 'AUTO',
    // Uses default Matchering settings
  },

  rock: {
    name: 'Rock',
    description: 'Punchy drums, clear guitars, dynamic range',
    icon: 'ROCK',
    threshold: 0.96,           // Moderate limiting for punch
    lowessFrac: 0.06,         // Increased from 0.05 - smoother EQ, less harsh
    lowessIt: 4,              // Increased from 3 - more smoothing
    fftSize: 4096,            // Standard resolution
    useLimiter: true,
    normalize: true,
  },

  edm: {
    name: 'EDM/Electronic',
    description: 'Maximum loudness, tight bass, bright highs',
    icon: 'EDM',
    threshold: 0.99,           // Maximum loudness
    lowessFrac: 0.05,         // Increased from 0.03 - less aggressive EQ
    lowessIt: 3,              // Increased from 2 - smoother curve
    fftSize: 8192,            // Higher resolution for electronic music
    useLimiter: true,
    normalize: true,
  },

  jazz: {
    name: 'Jazz',
    description: 'Natural dynamics, warm tone, spacious',
    icon: 'JAZZ',
    threshold: 0.88,           // Preserve dynamics
    lowessFrac: 0.08,         // Smooth EQ curve
    lowessIt: 5,              // More iterations for smoothness
    fftSize: 4096,
    useLimiter: true,
    normalize: false,          // Preserve natural dynamics
  },

  hiphop: {
    name: 'Hip-Hop',
    description: 'Heavy bass, clear vocals, punchy drums',
    icon: 'HIP-HOP',
    threshold: 0.97,           // High loudness with some dynamics
    lowessFrac: 0.06,         // Increased from 0.04 - smoother, less harsh
    lowessIt: 4,              // Increased from 3 - more smoothing
    fftSize: 4096,
    useLimiter: true,
    normalize: true,
  },

  classical: {
    name: 'Classical',
    description: 'Maximum dynamics, natural tone, subtle processing',
    icon: 'CLASSICAL',
    threshold: 0.80,           // Minimal limiting
    lowessFrac: 0.10,         // Very smooth EQ
    lowessIt: 6,              // Maximum smoothness
    fftSize: 8192,            // High resolution for detail
    useLimiter: false,         // No limiting
    normalize: false,          // Preserve full dynamics
  },

  pop: {
    name: 'Pop',
    description: 'Balanced, radio-ready, vocal-forward',
    icon: 'POP',
    threshold: 0.95,           // Modern pop loudness
    lowessFrac: 0.06,         // Increased from 0.05 - smoother EQ
    lowessIt: 4,              // Increased from 3 - less harsh
    fftSize: 4096,
    useLimiter: true,
    normalize: true,
  },

  metal: {
    name: 'Metal',
    description: 'Aggressive, tight, maximum impact',
    icon: 'METAL',
    threshold: 0.98,           // Very loud
    lowessFrac: 0.05,         // Increased from 0.04 - slightly smoother
    lowessIt: 3,              // Increased from 2 - less harsh peaks
    fftSize: 4096,
    useLimiter: true,
    normalize: true,
  },

  acoustic: {
    name: 'Acoustic',
    description: 'Natural, warm, organic dynamics',
    icon: 'ACOUSTIC',
    threshold: 0.90,           // Moderate limiting
    lowessFrac: 0.07,         // Smooth EQ
    lowessIt: 4,
    fftSize: 4096,
    useLimiter: true,
    normalize: false,
  },

  latin: {
    name: 'Latin',
    description: 'Vibrant percussion, warm bass, energetic',
    icon: 'LATIN',
    threshold: 0.96,           // High energy with dynamics
    lowessFrac: 0.06,         // Smooth but defined EQ
    lowessIt: 4,              // Balanced smoothing
    fftSize: 4096,
    useLimiter: true,
    normalize: true,
  },

  applemaster: {
    name: 'Apple Digital Masters',
    description: 'ADM spec: -1 dBTP, 96kHz, high dynamics',
    icon: 'ADM',
    threshold: 0.90,           // Conservative for -1 dBTP headroom
    lowessFrac: 0.08,         // Very smooth EQ curve
    lowessIt: 5,              // Maximum smoothing for quality
    fftSize: 8192,            // High resolution analysis
    useLimiter: true,         // Gentle limiting
    normalize: true,          // Normalize to -1 dBTP
  },

  custom: {
    name: 'Custom',
    description: 'Manually configure all parameters',
    icon: 'CUSTOM',
    // User provides all settings
  },
};

/**
 * Get preset configuration for a genre
 */
export function getGenrePreset(genre: Genre): GenrePresetConfig {
  return GENRE_PRESETS[genre] || GENRE_PRESETS.auto;
}

/**
 * Apply genre preset to processing config
 */
export function applyGenrePreset(genre: Genre, baseConfig: any = {}): any {
  const preset = getGenrePreset(genre);

  if (genre === 'auto' || genre === 'custom') {
    return baseConfig;
  }

  return {
    ...baseConfig,
    genre,
    threshold: preset.threshold,
    lowessFrac: preset.lowessFrac,
    lowessIt: preset.lowessIt,
    fftSize: preset.fftSize,
    useLimiter: preset.useLimiter ?? true,
    normalize: preset.normalize ?? true,
  };
}

/**
 * Get all available genres for UI
 */
export function getAvailableGenres(): Array<{ value: Genre; label: string; description: string; icon: string }> {
  return Object.entries(GENRE_PRESETS).map(([key, preset]) => ({
    value: key as Genre,
    label: preset.name,
    description: preset.description,
    icon: preset.icon,
  }));
}
